import React, { useState, useRef } from "react";
import AvatarEditor from "react-avatar-editor";
import { useUsers } from "../hooks/useUsers/useUsers";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";

interface ImageUploaderProps {
  email: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const ImageUploader = ({ email, onSuccess, onCancel }: ImageUploaderProps) => {
  const [src, setSrc] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const editorRef = useRef<AvatarEditor | null>(null);
  const { updateAvatar } = useUsers();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Handle image file upload
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSrc(reader.result as string); // Cast to string since FileReader returns a string or null
      };
      reader.readAsDataURL(file);
      setImageFile(file);
      setError(null);
    }
  };

  // Handle submit to backend
  const handleSubmit = async (): Promise<void> => {
    if (loading || !editorRef.current || !imageFile) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    const canvas = editorRef.current.getImageScaledToCanvas();
    canvas.toBlob(async (blob) => {
      if (!blob) {
        setError("Failed to create a blob from the canvas");
        setLoading(false);
        return;
      }
      const response = await updateAvatar(email, blob, imageFile.name);
      if (response instanceof Error) {
        setError(response.message);
        setLoading(false);
        return;
      }
      if (onSuccess) {
        onSuccess();
      } else {
        setSuccess("Profile image uploaded successfully!");
        setSrc(null);
        setImageFile(null);
      }
    }, "image/jpeg");
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: isMobile ? 2 : 3,
        bgcolor: "background.paper",
        maxWidth: 400,
        mx: "auto",
        borderRadius: 2,
        boxShadow: theme.shadows[3],
        position: "relative",
      }}
    >
      <Stack spacing={3} alignItems="center">
        <Typography variant="h6" sx={{ mb: 1 }}>
          Upload New Profile Image
        </Typography>
        {error && (
          <Alert severity="error" sx={{ width: "100%" }}>
            Error uploading new profile image: {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ width: "100%" }}>
            {success}
          </Alert>
        )}
        <Button
          variant="outlined"
          component="label"
          fullWidth={isMobile}
          sx={{ textTransform: "none" }}
        >
          Choose Image
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={handleImageChange}
            disabled={loading}
          />
        </Button>
        {src && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                border: `2px solid ${theme.palette.divider}`,
                borderRadius: 2,
                boxShadow: theme.shadows[1],
                mb: 2,
                bgcolor: theme.palette.background.default,
                position: "relative",
                width: 250,
                height: 250,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AvatarEditor
                ref={editorRef}
                image={src}
                width={250}
                height={250}
                border={0}
                scale={1.2}
                rotate={0}
                style={{ borderRadius: 8, background: "transparent" }}
              />
              {loading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    bgcolor: "rgba(0,0,0,0.3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 2,
                    zIndex: 2,
                  }}
                >
                  <CircularProgress color="primary" />
                </Box>
              )}
            </Box>
            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={2}
              sx={{ width: "100%" }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading || !imageFile}
                fullWidth={isMobile}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {loading ? "Uploading..." : "Upload Image"}
              </Button>
              {onCancel && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onCancel}
                  disabled={loading}
                  fullWidth={isMobile}
                >
                  Cancel
                </Button>
              )}
            </Stack>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default ImageUploader;
